import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { createAccountExpense, updateAccountExpense, getAccountExpenseById, getAccountVendors, getAccountCustomers } from '../core/_requests';
import SearchableSelect from '../../../shared/components/SearchableSelect'

export default function AccountsNewExpense() {
  const navigate = useNavigate();
  const { id } = useParams();
  const expenseId = id ? Number(id) : null;
  const isEditing = expenseId !== null && !Number.isNaN(expenseId);

  const [saving, setSaving] = useState(false);
  const [loadingInitial, setLoadingInitial] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);

  // External Data
  const [vendors, setVendors] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);

  // Form State
  const [expenseDate, setExpenseDate] = useState(new Date().toISOString().split('T')[0]);
  const [account, setAccount] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [vendorId, setVendorId] = useState('');
  const [customerId, setCustomerId] = useState('');
  const [amount, setAmount] = useState('');
  const [taxName, setTaxName] = useState('');
  const [taxAmount, setTaxAmount] = useState('');
  const [paidThrough, setPaidThrough] = useState('Petty Cash');
  const [notes, setNotes] = useState('');

  useEffect(() => {
    // Load vendors and customers
    Promise.all([
      getAccountVendors().catch(() => []),
      getAccountCustomers().catch(() => [])
    ]).then(([vData, cData]) => {
      setVendors(vData || []);
      setCustomers(cData || []);
    });
  }, []);

  useEffect(() => {
    if (!isEditing || expenseId == null) return;
    setLoadingInitial(true);
    getAccountExpenseById(expenseId).then(data => {
      if (data) {
        setExpenseDate(data.expenseDate || data.expense_date || new Date().toISOString().split('T')[0]);
        setAccount(data.account || '');
        setReferenceNumber(data.referenceNumber || data.reference_number || '');
        setVendorId(data.vendorId ? String(data.vendorId) : '');
        setCustomerId(data.customerId ? String(data.customerId) : '');
        setAmount(data.amount !== undefined ? String(data.amount) : '');
        setTaxName(data.taxName || data.tax_name || '');
        setTaxAmount(data.taxAmount !== undefined ? String(data.taxAmount) : '');
        setPaidThrough(data.paidThrough || data.paid_through || 'Petty Cash');
        setNotes(data.notes || '');
      }
    }).catch(console.error).finally(() => setLoadingInitial(false));
  }, [isEditing, expenseId]);

  const handleSave = async () => {
    if (!expenseDate || !account || !amount) {
      alert("Please fill in all required fields (Date, Account, Amount).");
      return;
    }
    
    setSaving(true);
    const selectedVendor = vendors.find(v => v.id === Number(vendorId));
    const selectedCustomer = customers.find(c => c.id === Number(customerId));

    const payload = {
      expenseDate,
      account,
      referenceNumber,
      vendorId: vendorId ? Number(vendorId) : null,
      vendorName: selectedVendor ? selectedVendor.displayName : null,
      customerId: customerId ? Number(customerId) : null,
      customerName: selectedCustomer ? selectedCustomer.displayName : null,
      amount: Number(amount),
      taxName,
      taxAmount: taxAmount ? Number(taxAmount) : 0,
      paidThrough,
      notes
    };

    try {
      if (isEditing && expenseId) {
        await updateAccountExpense(expenseId, payload);
      } else {
        await createAccountExpense(payload);
      }
      navigate('/accounts/purchase/expenses');
    } catch (err: any) {
      console.error('Save failed:', err);
      alert('Failed to save expense');
    } finally {
      setSaving(false);
    }
  };

  const inputClass = "w-full px-3.5 py-2.5 bg-white border border-slate-200 rounded-xl text-sm text-slate-700 outline-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-50 transition-all";

  if (loadingInitial) {
    return (
      <div className="flex h-screen w-full items-center justify-center bg-slate-50">
        <p className="text-slate-500 font-medium">Loading Expense...</p>
      </div>
    );
  }

  return (
    <>
      <div className="flex h-screen w-full overflow-hidden bg-slate-50">
        
        <div id="sidebar-overlay" className="fixed inset-0 bg-black/50 z-40 hidden md:hidden"></div>
        <main className="flex-1 flex flex-col h-full overflow-hidden relative">
          
          <div className="flex-1 overflow-y-auto overflow-x-hidden p-4 md:p-6" id="main-scroll">
            <div className="max-w-5xl mx-auto pb-20">
              <div className="mb-6">
                <div className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Accounts / Purchase / Expenses</div>
                <h1 className="text-2xl font-bold text-slate-900 leading-tight">
                  {isEditing ? 'Edit Expense' : 'New Expense'}
                </h1>
                <p className="text-sm text-slate-500">
                  {isEditing ? 'Update the details of your expense' : 'Record a new expense'}
                </p>
              </div>
              
              <form ref={formRef} onSubmit={(e) => { e.preventDefault(); handleSave(); }} className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Date <span className="text-red-500">*</span></label>
                    <input type="date" value={expenseDate} onChange={e => setExpenseDate(e.target.value)} required className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Expense Account <span className="text-red-500">*</span></label>
                    <SearchableSelect value={account} onChange={e => setAccount(e.target.value)} required className={inputClass}>
                      <option value="">Select an account</option>
                      <option value="Advertising And Marketing">Advertising And Marketing</option>
                      <option value="Automobile Expense">Automobile Expense</option>
                      <option value="IT and Internet Expenses">IT and Internet Expenses</option>
                      <option value="Lodging">Lodging</option>
                      <option value="Meals and Entertainment">Meals and Entertainment</option>
                      <option value="Office Supplies">Office Supplies</option>
                      <option value="Travel Expense">Travel Expense</option>
                      <option value="Other Expenses">Other Expenses</option>
                    </SearchableSelect>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Amount <span className="text-red-500">*</span></label>
                    <input type="number" step="0.01" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0.00" required className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Paid Through <span className="text-red-500">*</span></label>
                    <SearchableSelect value={paidThrough} onChange={e => setPaidThrough(e.target.value)} required className={inputClass}>
                      <option value="Petty Cash">Petty Cash</option>
                      <option value="Undeposited Funds">Undeposited Funds</option>
                      <option value="Bank Account">Bank Account</option>
                      <option value="Credit Card">Credit Card</option>
                    </SearchableSelect>
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Tax Name</label>
                    <input type="text" value={taxName} onChange={e => setTaxName(e.target.value)} placeholder="e.g. VAT" className={inputClass} />
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Tax Amount</label>
                    <input type="number" step="0.01" value={taxAmount} onChange={e => setTaxAmount(e.target.value)} placeholder="0.00" className={inputClass} />
                  </div>
                </div>

                <hr className="my-6 border-slate-100" />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Vendor</label>
                    <SearchableSelect value={vendorId} onChange={e => setVendorId(e.target.value)} className={inputClass}>
                      <option value="">Select a vendor...</option>
                      {vendors.map(v => (
                        <option key={v.id} value={v.id}>{v.displayName}</option>
                      ))}
                    </SearchableSelect>
                  </div>
                  <div className="mb-5">
                    <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Reference#</label>
                    <input type="text" value={referenceNumber} onChange={e => setReferenceNumber(e.target.value)} placeholder="Reference Number" className={inputClass} />
                  </div>
                </div>

                <div className="mb-5 max-w-md">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Customer Name</label>
                  <SearchableSelect value={customerId} onChange={e => setCustomerId(e.target.value)} className={inputClass}>
                    <option value="">Select a customer...</option>
                    {customers.map(c => (
                      <option key={c.id} value={c.id}>{c.displayName}</option>
                    ))}
                  </SearchableSelect>
                </div>

                <div className="mb-5">
                  <label className="block text-xs font-semibold text-slate-600 mb-1.5 uppercase tracking-wider">Notes</label>
                  <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3} placeholder="Max. 500 characters" className={`${inputClass} resize-none`}></textarea>
                </div>

                <div className="pt-6 mt-6 border-t border-slate-100 flex items-center gap-3">
                  <button type="submit" disabled={saving} className="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-bold shadow-sm hover:shadow hover:-translate-y-0.5 transition-all disabled:opacity-50">
                    {saving ? 'Saving...' : 'Save Expense'}
                  </button>
                  <button type="button" onClick={() => navigate(-1)} className="px-6 py-2.5 text-slate-500 hover:text-slate-700 text-sm font-bold transition-colors">
                    Cancel
                  </button>
                </div>
              </form>
            </div>
            
          </div>
        </main>
      </div>
      
    </>
  );
}

